import ReactDataGrid from "@inovua/reactdatagrid-community";
import { Box, Button, ButtonGroup, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import Modal from "components/Modal";
import { useSnackbar } from "notistack";
import InfoCard from "pages/dashboard/InfoCard";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getLibrariesPendingApprovalByStage } from "services/vendorAnalytics";
import VendorInfo from "./VendorInfo";

const stageList = [
  {name: 'Stage 1', id: 'basicDetails', color: 'warning'},
  {name: 'Stage 2', id: 'detailsMissing', color: 'info'},
  {name: 'Stage 3', id: 'incomplete', color: 'success'},
];

const defaultFilterValue = [
  { name: 'name', operator: 'contains', type: 'string', value: '' },
  { name: 'city', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    return data?.address?.city?.toLowerCase()?.includes(filterValue.toLowerCase());
  }},
  { name: 'address', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    const addressArr = [];
    addressArr.push(data?.address?.area);
    addressArr.push(data?.address?.city);
    addressArr.push(data?.address?.state);
    addressArr.push(data?.address?.country);
    addressArr.push(data?.address?.pin);
    const address = (addressArr.filter((ele)=> !!ele ).join(', ') || ''); 

    return address.toLowerCase().includes(filterValue.toLowerCase());
  }},
  { name: 'area', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    return data?.address?.area?.toLowerCase()?.includes(filterValue.toLowerCase());
  }},
  { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
];

const defaultFilterStage = 'basicDetails';
export default function InProgressPage(){
  const gridStyle = useMemo(() => ({ minHeight: 380, width: '100%' }), []);
  const { enqueueSnackbar } = useSnackbar();
  const [libraries, setLibraries] = useState({});
  const [filterStatus, setFilterStatus] = useState(defaultFilterStage);
  const [tableData, setTableData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [vendorId, setVendorId] = useState(false);

  const columns = useMemo(() => ([
    { name: 'name', header: 'Name', width: 240 },
    { name: 'city', header: 'City', width: 150, render: ({data} ) => data?.address?.city || '' },
    { name: 'area', header: 'Area', width: 200, render: ({data} ) => data?.address?.area || '' },
    { name: 'address', header: 'Address', width: 400, render: ({data} ) => {
      const addressArr = [];
      addressArr.push(data?.address?.area);
      addressArr.push(data?.address?.city);
      addressArr.push(data?.address?.state);
      addressArr.push(data?.address?.country);
      addressArr.push(data?.address?.pin);
      return (addressArr.filter((ele)=> !!ele ).join(', ') || ''); 
    }},
    { name: 'createdOn', header: 'Created On', width: 120 },
    { name: 'actions', header: 'Actions', width: 180, render: ({data}) => {
      return (
        <Stack direction="row" spacing={2}>
          <Box>
            <Button variant="contained" size="small" color="info" sx={{textTransform: 'none'}} onClick={() => setVendorId(data?.createdBy)}>
              Vendor Details
            </Button>
          </Box>
        </Stack>
      )
    }}
  ]), []);

  const getLibraries = useCallback((filterStatus) => {
    getLibrariesPendingApprovalByStage().then(({response, error}) => {
      if(response){
        setLibraries({...response});
        setTableData([...response[filterStatus]]);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar]);
  
  useEffect(() => {
    getLibraries(defaultFilterStage);
  }, [getLibraries]);

  useEffect(() => {
    if(filterStatus && libraries[filterStatus]){
      setTableData([...libraries[filterStatus]]);
    }else{
      setTableData([]);
    }
  }, [filterStatus, libraries]);

  const onModalClose = useCallback(() => {
    setOpenModal(false);
    setVendorId(null);
  }, [setOpenModal]);

  useEffect(() => {
    if(vendorId){
      setOpenModal(true);
    }else{
      onModalClose();
    }    
  }, [vendorId, onModalClose]);
  
  const onSuccess = useCallback(() => {
    onModalClose();
  }, [onModalClose]);
  
    
    return (
      <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      {/* <CardContent> */}
        <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
          Libraries
        </Typography>
        <Divider />
        <Box noValidate  sx={{ mt: 2 }} >
          <Grid container spacing={3} justifyContent="center">
            <Grid container item xs={12} sm={12} md={12} lg={11} xl={10} spacing={3}>
              <Grid item xs={12} sm={6} md={4} xl={4} lg={4}>
                <InfoCard title="Libraries with basic info" theme="orange" value={libraries.basicDetailCount} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={4} lg={4}>
                <InfoCard title="Libraries with operational info" theme="blue" value={libraries.detailsMissingCount} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={4} lg={4}>
                <InfoCard title="Libraries with missing KYC, Images etc" theme="green" value={libraries.incompleteCount} />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={11} xl={10} mt={2} textAlign={{xs: "center", sm: "right"}}>
              <ButtonGroup variant="outlined" disableElevation sx={{ display:{xs: 'block', sm: 'block'} }} >
                  {stageList.map(({id, name, color='info'}) => (<Button sx={{textTransform: 'none'}} color={color} variant={filterStatus === id ? "contained" : 'outlined'} key={id} onClick={() => setFilterStatus(id)}>{name}</Button>))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Box component="div" sx={{  mt: 3 ,  maxHeight: 720, width: '100%' }}>
                <ReactDataGrid
                  // handle={setGridRef}
                  // idProperty="index"
                  style={gridStyle}
                  defaultFilterValue={defaultFilterValue}
                  pagination="local"
                  columns={columns}
                  dataSource={tableData}
                  rowHeight={null} 
                  minRowHeight={50}
                  defaultLimit={5}
                />
              </Box>
            </Grid>
          </Grid>
        </Box> 
        <Modal  maxWidth={"sm"} open={openModal} onModalClose={onModalClose} title={'Vendor Details'}>
          <VendorInfo vendorId={vendorId} onSuccess={onSuccess} onFailure={onModalClose} />
        </Modal>
      </Paper>
    );
  }
